import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { eq, not, or } from 'tio-ui/utilities';
import Chip from 'tio-reimagined/components/chip';
import { formatAsDollars, toLocaleDateString } from '../../../utils/format.ts';
import {
  generateEligibilityBlurb,
  generateLimitsSummary,
  PARTICIPANT_STATE_LABELS,
  LIMIT_LEVEL_LABELS,
  EMPLOYMENT_TYPE_LABELS,
} from '../../../utils/approval-decision-blurb.ts';
import {
  PhShieldCheck,
  PhCheckCircle,
  PhWarningCircle,
  PhXCircle,
  PhCaretDown,
  PhCaretUp,
  PhCurrencyDollar,
  PhUserCheck,
  PhBriefcase,
} from 'ember-phosphor-icons';
import type {
  LimitsStatusDataSignature,
  LimitsDetailSignature,
  EligibilityStatusSignature,
} from '../../../services/tuition-assistance.ts';

// ---------------------------------------------------------------------------
// ApprovalDecisionBanner – decision-assist panel for approvers
//
// Design principles:
//   1. Sub-second scannability — approvers process many apps per day
//   2. Color signals status before text is read (green/amber/red accents)
//   3. Value-first hierarchy — the answer is prominent, context is secondary
//   4. Progressive disclosure — cascade details on demand
// ---------------------------------------------------------------------------

interface ApprovalDecisionBannerSignature {
  Args: {
    eligibilityData?: EligibilityStatusSignature;
    limitsData?: LimitsStatusDataSignature;
    isLoading?: boolean;
    periodsOfEligibility?: Array<[string, string]>;
    isPostPaymentReview?: boolean;
  };
  Element: HTMLDivElement;
}

const participantStateLabel = (state?: string | null) => {
  if (!state) return 'Unknown';
  return PARTICIPANT_STATE_LABELS[state] ?? state.split('.').pop() ?? 'Unknown';
};

const limitLevelLabel = (level: string) => {
  return LIMIT_LEVEL_LABELS[level] ?? level;
};

class ApprovalDecisionBanner extends Component<ApprovalDecisionBannerSignature> {
  @tracked showCascade = false;
  @tracked showDetails = false;

  // ── Participant enrollment ──

  get participantState() {
    return this.args.eligibilityData?.participant_state;
  }

  get participantLabel() {
    return participantStateLabel(this.participantState);
  }

  get isParticipantOk() {
    const state = this.participantState;
    return state === 'TAS.ParticipantState.ACTIVE' || state === 'TAS.ParticipantState.ELIGIBLE';
  }

  get isParticipantWarning() {
    return this.participantState === 'TAS.ParticipantState.HELD';
  }

  get participantDetail() {
    const data = this.args.eligibilityData;
    if (!data) return '';
    if (data.lock_basis && data.lock_date) {
      const basisLabel = data.lock_basis.replace(/_/g, ' ').toLowerCase();
      return `Locked at ${basisLabel} (${toLocaleDateString(data.lock_date)})`;
    }
    return '';
  }

  // ── Employment ──

  get isDeactivated() {
    return !!this.args.eligibilityData?.deactivated_on;
  }

  get employmentValue() {
    if (!this.args.eligibilityData) return 'Unknown';
    if (!this.isDeactivated) return 'Active';
    if (this.args.eligibilityData.pay_deactivated_employees) return 'Deactivated: Payable';
    return 'Inactive';
  }

  get employmentTypeLabel() {
    const raw = this.args.eligibilityData?.employment_type;
    if (!raw) return '';
    return (
      EMPLOYMENT_TYPE_LABELS[raw] ??
      raw
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  get isEmploymentOk() {
    if (!this.args.eligibilityData) return false;
    return !this.isDeactivated;
  }

  get isEmploymentWarning() {
    return this.isDeactivated && !!this.args.eligibilityData?.pay_deactivated_employees;
  }

  get employmentDetail() {
    const data = this.args.eligibilityData;
    if (!data?.deactivated_on) return '';
    const date = toLocaleDateString(data.deactivated_on);
    return data.pay_deactivated_employees ? `Since ${date} · company pays` : `Since ${date}`;
  }

  // ── Budget ──

  get isCredits() {
    return this.args.limitsData?.limit_capacity === 'TAS.LimitCapacity.CREDITS';
  }

  get budgetAvailable() {
    return this.args.limitsData?.available;
  }

  get benefitLabel() {
    const available = this.budgetAvailable;
    // null means unlimited/no configured limit — treat as a positive state
    if (available === null) return 'Unlimited';
    if (available === undefined) return 'No limit';
    if (this.isCredits) return `${available} credit${available === 1 ? '' : 's'}`;
    return formatAsDollars(available);
  }

  get hasBenefit() {
    const available = this.budgetAvailable;
    // null represents an unlimited/no-limit program — treat as having benefit
    if (available === null) return true;
    // undefined means missing/unknown data
    if (available === undefined) return false;
    return available > 0;
  }

  // When post-payment review and benefit is exhausted, show amber instead of red.
  // If hasBenefit is true (available > 0), green is still accurate — benefit remains.
  get isBudgetWarning() {
    return this.args.isPostPaymentReview && !this.hasBenefit;
  }

  get constrainingLevel() {
    return this.args.limitsData?.soft_limit ?? 'none';
  }

  get constrainingLevelLabel() {
    return limitLevelLabel(this.constrainingLevel);
  }

  get benefitPeriod() {
    const start = this.args.limitsData?.annual_start_date;
    const end = this.args.limitsData?.annual_end_date;
    if (!start || !end) return '';
    return `${toLocaleDateString(start)} – ${toLocaleDateString(end)}`;
  }

  // ── Overall ──

  get hasEligibilityData() {
    return this.args.eligibilityData !== undefined;
  }

  get isEligible() {
    return this.args.eligibilityData?.is_eligible ?? false;
  }

  get ignoresPolicies() {
    return this.args.eligibilityData?.ignore_policies ?? false;
  }

  get accentBorderClass() {
    if (this.ignoresPolicies) return 'border-l-amber-400';
    if (!this.hasEligibilityData) return 'border-l-gray-400';
    if (this.isEligible) return 'border-l-emerald-500';
    return 'border-l-red-500';
  }

  // ── Cascade ──

  get cascadeLevels() {
    const details = this.args.limitsData?.details;
    if (!details) return [];

    const softLimit = this.args.limitsData?.soft_limit ?? 'none';
    const isCredits = this.isCredits;

    const fmt = (cents: number) => {
      if (isCredits) return `${cents} cr`;
      return formatAsDollars(cents);
    };

    const order: Array<{ key: keyof typeof details; label: string }> = [
      { key: 'scheme', label: 'Program' },
      { key: 'batch', label: 'Batch' },
      { key: 'product', label: 'Product' },
      { key: 'company', label: 'Company' },
    ];

    return order.map(({ key, label }) => {
      const detail: LimitsDetailSignature | undefined = details[key];
      const isConstraining = softLimit === key || softLimit === `${key}_annual`;
      const limit = detail?.annual_limit;
      const available = detail?.available;
      return {
        key,
        label,
        hasLimit: limit != null,
        limitFormatted: limit != null ? fmt(limit) : null,
        usageFormatted: fmt(detail?.annual_usage ?? 0),
        hasAvailable: available != null,
        availableFormatted: available != null ? fmt(available) : null,
        isConstraining,
      };
    });
  }

  // ── Blurb ──

  get eligibilityBlurb() {
    const data = this.args.eligibilityData;
    if (!data) return '';
    return generateEligibilityBlurb(data);
  }

  get limitsSummary() {
    const data = this.args.limitsData;
    if (!data) return '';
    return generateLimitsSummary(data);
  }

  get detailBlurb() {
    const parts = [this.eligibilityBlurb, this.limitsSummary].filter(Boolean);
    return parts.join(' ');
  }

  get formattedPeriods() {
    const periods = this.args.periodsOfEligibility;
    if (!periods || periods.length === 0) return [];
    return periods.map(([start, end]: [string, string]) => ({
      range: `${toLocaleDateString(start)} – ${toLocaleDateString(end)}`,
    }));
  }

  toggleCascade = () => {
    this.showCascade = !this.showCascade;
  };

  toggleDetails = () => {
    this.showDetails = !this.showDetails;
  };

  <template>
    {{!
      The outer div uses a thick left border as a "traffic light" accent.
      Approvers see green/amber/red before reading a single word.
    }}
    <div
      data-test-approval-decision-banner
      class="rounded-lg border border-gray-200 bg-white shadow-sm border-l-4 overflow-hidden
        {{this.accentBorderClass}}"
      ...attributes
    >
      {{! ── Header bar ── }}
      <div
        class="flex items-center justify-between px-5 py-3 bg-gray-50/60 border-b border-gray-100"
      >
        <div class="flex items-center gap-2">
          <PhShieldCheck @size="18" class="text-violet-600" />
          <span class="text-sm font-semibold text-gray-800 tracking-tight">
            Approval Decision Assist
          </span>
        </div>

        {{#if this.ignoresPolicies}}
          <Chip @intent="warning" @size="sm" @appearance="faded">Admin Override</Chip>
        {{else if (not this.hasEligibilityData)}}
          <div class="flex items-center gap-1.5">
            <PhWarningCircle @size="16" class="text-gray-400" />
            <span class="text-sm font-semibold text-gray-500">Unknown</span>
          </div>
        {{else if this.isEligible}}
          <div class="flex items-center gap-1.5">
            <PhCheckCircle @size="16" class="text-emerald-600" />
            <span class="text-sm font-semibold text-emerald-700">Eligible</span>
          </div>
        {{else}}
          <div class="flex items-center gap-1.5">
            <PhXCircle @size="16" class="text-red-600" />
            <span class="text-sm font-semibold text-red-700">Ineligible</span>
          </div>
        {{/if}}
      </div>

      {{! ── Body ── }}
      <div class="px-5 py-4">
        {{#if @isLoading}}
          {{! Skeleton loading state — three pulsing tiles }}
          <div class="grid grid-cols-3 gap-3">
            <div class="animate-pulse rounded-lg bg-gray-100 h-20"></div>
            <div class="animate-pulse rounded-lg bg-gray-100 h-20 delay-75"></div>
            <div class="animate-pulse rounded-lg bg-gray-100 h-20 delay-150"></div>
          </div>
        {{else}}
          {{! ── Three stat tiles ── }}
          <div class="grid grid-cols-3 gap-3">

            {{! Tile 1: Program Eligibility }}
            <div
              class="rounded-lg border px-3.5 py-3
                {{if
                  this.isParticipantOk
                  'border-emerald-200 bg-emerald-50/50'
                  (if
                    this.isParticipantWarning
                    'border-amber-200 bg-amber-50/50'
                    'border-red-200 bg-red-50/50'
                  )
                }}"
            >
              <div class="flex items-center gap-1.5 mb-2">
                <PhUserCheck
                  @size="14"
                  class="{{if
                      this.isParticipantOk
                      'text-emerald-600'
                      (if this.isParticipantWarning 'text-amber-600' 'text-red-600')
                    }}"
                />
                <span class="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Eligibility
                </span>
              </div>
              <div
                class="text-lg font-bold leading-tight
                  {{if
                    this.isParticipantOk
                    'text-emerald-800'
                    (if this.isParticipantWarning 'text-amber-800' 'text-red-800')
                  }}"
              >
                {{this.participantLabel}}
              </div>
              {{#if this.participantDetail}}
                <div class="text-[11px] text-gray-500 mt-1 leading-tight">
                  {{this.participantDetail}}
                </div>
              {{/if}}
            </div>

            {{! Tile 2: Employment }}
            <div
              class="rounded-lg border px-3.5 py-3
                {{if
                  this.isEmploymentOk
                  'border-emerald-200 bg-emerald-50/50'
                  (if
                    this.isEmploymentWarning
                    'border-amber-200 bg-amber-50/50'
                    'border-red-200 bg-red-50/50'
                  )
                }}"
            >
              <div class="flex items-center gap-1.5 mb-2">
                <PhBriefcase
                  @size="14"
                  class="{{if
                      this.isEmploymentOk
                      'text-emerald-600'
                      (if this.isEmploymentWarning 'text-amber-600' 'text-red-600')
                    }}"
                />
                <span class="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Employment
                </span>
              </div>
              <div
                class="text-lg font-bold leading-tight
                  {{if
                    this.isEmploymentOk
                    'text-emerald-800'
                    (if this.isEmploymentWarning 'text-amber-800' 'text-red-800')
                  }}"
              >
                {{this.employmentValue}}
              </div>
              {{#if this.employmentTypeLabel}}
                <div class="text-[11px] text-gray-500 mt-1 leading-tight">
                  {{this.employmentTypeLabel}}
                </div>
              {{/if}}
              {{#if this.employmentDetail}}
                <div class="text-[11px] text-gray-500 mt-1 leading-tight">
                  {{this.employmentDetail}}
                </div>
              {{/if}}
            </div>

            {{! Tile 3: Benefit }}
            <div
              class="rounded-lg border px-3.5 py-3
                {{if
                  this.hasBenefit
                  'border-emerald-200 bg-emerald-50/50'
                  (if
                    this.isBudgetWarning
                    'border-amber-200 bg-amber-50/50'
                    'border-red-200 bg-red-50/50'
                  )
                }}"
            >
              <div class="flex items-center gap-1.5 mb-2">
                <PhCurrencyDollar
                  @size="14"
                  class="{{if
                      this.hasBenefit
                      'text-emerald-600'
                      (if this.isBudgetWarning 'text-amber-600' 'text-red-600')
                    }}"
                />
                <span class="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Benefit
                </span>
              </div>
              <div
                class="text-lg font-bold leading-tight
                  {{if
                    this.hasBenefit
                    'text-emerald-800'
                    (if this.isBudgetWarning 'text-amber-800' 'text-red-800')
                  }}"
              >
                {{this.benefitLabel}}
              </div>
              {{#if this.isBudgetWarning}}
                <div class="text-[11px] text-amber-700 mt-1 leading-tight">
                  {{t "common.tas.approval.post_payment_review.already_paid"}}
                </div>
              {{else}}
                {{#unless (eq this.constrainingLevel "none")}}
                  <div class="text-[11px] text-gray-500 mt-1 leading-tight">
                    {{this.constrainingLevelLabel}}
                  </div>
                {{/unless}}
              {{/if}}
              {{#if this.benefitPeriod}}
                <div class="text-[11px] text-gray-500 mt-1 leading-tight whitespace-nowrap">
                  {{this.benefitPeriod}}
                </div>
              {{/if}}
            </div>
          </div>

          {{! ── View Details disclosure ── }}
          {{#if (or this.detailBlurb this.formattedPeriods.length)}}
            <div class="mt-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                class="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors group"
                data-test-details-toggle
                aria-expanded={{if this.showDetails "true" "false"}}
                aria-controls="approval-details-content"
                {{on "click" this.toggleDetails}}
              >
                {{#if this.showDetails}}
                  <PhCaretUp @size="12" class="transition-transform" />
                {{else}}
                  <PhCaretDown @size="12" class="transition-transform" />
                {{/if}}
                <span class="group-hover:underline">View details</span>
              </button>

              {{#if this.showDetails}}
                <div id="approval-details-content">
                  {{#if this.detailBlurb}}
                    <p class="mt-2 text-xs text-gray-500 leading-relaxed" data-test-detail-blurb>
                      {{this.detailBlurb}}
                    </p>
                  {{/if}}
                  {{#if this.formattedPeriods.length}}
                    <div class="mt-2" data-test-eligibility-periods>
                      <p class="text-xs font-medium text-gray-500 mb-1">Program eligibility periods</p>
                      {{#each this.formattedPeriods as |period|}}
                        <p class="text-xs text-gray-500" data-test-eligibility-period>
                          {{period.range}}
                        </p>
                      {{/each}}
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{! ── Limits Cascade (expandable) ── }}
          {{#if this.cascadeLevels.length}}
            <div class="mt-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                class="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors group"
                data-test-cascade-toggle
                aria-expanded={{if this.showCascade "true" "false"}}
                aria-controls="approval-cascade-content"
                {{on "click" this.toggleCascade}}
              >
                {{#if this.showCascade}}
                  <PhCaretUp @size="12" class="transition-transform" />
                {{else}}
                  <PhCaretDown @size="12" class="transition-transform" />
                {{/if}}
                <span class="group-hover:underline">Limits Cascade</span>
              </button>

              {{#if this.showCascade}}
                <div
                  class="mt-2.5 rounded-lg border border-gray-100 overflow-hidden"
                  id="approval-cascade-content"
                  data-test-limits-cascade
                >
                  {{#each this.cascadeLevels as |level|}}
                    <div
                      class="flex items-center justify-between py-2 px-3 text-sm
                        {{if
                          level.isConstraining
                          'bg-amber-50 border-l-2 border-l-amber-400'
                          'bg-white border-l-2 border-l-transparent'
                        }}
                        [&:not(:last-child)]:border-b [&:not(:last-child)]:border-b-gray-100"
                      data-test-cascade-level
                    >
                      <div class="flex items-center gap-2">
                        {{#if level.isConstraining}}
                          <PhWarningCircle @size="14" class="text-amber-600 flex-shrink-0" />
                        {{/if}}
                        <span
                          class="font-medium
                            {{if level.isConstraining 'text-amber-800' 'text-gray-600'}}"
                        >
                          {{level.label}}
                        </span>
                      </div>
                      <div class="flex items-center gap-4 text-xs tabular-nums">
                        {{#if level.hasLimit}}
                          <div class="text-gray-400">
                            <span class="font-medium text-gray-500">Limit</span>
                            {{level.limitFormatted}}
                          </div>
                        {{/if}}
                        <div class="text-gray-400">
                          <span class="font-medium text-gray-500">Used</span>
                          {{level.usageFormatted}}
                        </div>
                        {{#if level.hasAvailable}}
                          <div
                            class="{{if
                                level.isConstraining
                                'text-amber-700 font-semibold'
                                'text-gray-500'
                              }}"
                          >
                            <span class="font-medium">Avail</span>
                            {{level.availableFormatted}}
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </div>
          {{/if}}
        {{/if}}
      </div>
    </div>
  </template>
}

export default ApprovalDecisionBanner;

export type { ApprovalDecisionBannerSignature };
